// frontend/src/pages/AdminDashboard.jsx
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext.jsx';
import ContentBlockEditor from '../components/ContentBlockEditor.jsx';
import ContentPromptEditor from '../components/ContentPromptEditor';
import UserManagementView from '../components/UserManagementView.jsx';
import UsageStatsView from '../components/UsageStatsView.jsx';
import TemplateLibrary from '../components/TemplateLibrary.jsx';
import TemplateEditor from '../components/TemplateEditor.jsx';
import TemplateComparison from '../components/TemplateComparison.jsx';
import TemplateTester from '../components/TemplateTester.jsx';
import logger from '../utils/logger';
import { PencilIcon, TrashIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon, UsersIcon, BookOpenIcon, XMarkIcon, ChartBarIcon, DocumentTextIcon, DocumentDuplicateIcon } from '@heroicons/react/24/solid';

// Helper function to strip HTML tags and decode entities for display
const stripHtmlForDisplay = (html) => {
  if (!html) return '';
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Get text content (strips all HTML tags)
  let text = tempDiv.textContent || tempDiv.innerText || '';
  
  // Decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  text = textarea.value;
  
  return text;
};

function AdminDashboard() {
  const { token, user, isAuthenticated, authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [modules, setModules] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [error, setError] = useState('');
  const [currentView, setCurrentView] = useState('chapters'); // State for current view
  // Template management state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [comparingTemplates, setComparingTemplates] = useState(null);
  const [testingTemplate, setTestingTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [templateRefreshKey, setTemplateRefreshKey] = useState(0);
  const [draggedUnit, setDraggedUnit] = useState(null);
  const [draggedModule, setDraggedModule] = useState(null);
  const [draggedChapter, setDraggedChapter] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverType, setDragOverType] = useState(null);
  const [movedItemId, setMovedItemId] = useState(null);
  const [movedItemType, setMovedItemType] = useState(null);
  const [panelWidths, setPanelWidths] = useState({ unit: 33.33, module: 33.33, chapter: 33.33 });
  // Document templates (deliverables)
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [editingDocumentTemplate, setEditingDocumentTemplate] = useState(null);
  const [documentTemplateForm, setDocumentTemplateForm] = useState({
    name: '',
    description: '',
    documentType: 'pdf',
    requiredChapterIds: [],
    adminInputs: {},
    adminInputsRaw: '{}',
    aiInstructions: '',
    outputStructure: null,
    isActive: true,
    order: 0
  });
  const [showDocumentTemplateModal, setShowDocumentTemplateModal] = useState(false);
  const [isResizing, setIsResizing] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // Can be unit, module, or chapter
  const [editingItemType, setEditingItemType] = useState(null); // 'unit', 'module', or 'chapter'
  const [newItemForm, setNewItemForm] = useState({
    name: '',
    title: '',
    description: '',
    moduleId: '',
    unitId: '',
    order: '',
    isRequired: true,
    color: 'gray'
  });
  const [editItemForm, setEditItemForm] = useState({
    name: '',
    title: '',
    description: '',
    moduleId: '',
    unitId: '',
    order: '',
    isRequired: true,
    color: 'gray'
  });

  // Security check: Ensure user is authenticated and is an admin
  useEffect(() => {
    // Wait for auth to be determined
    if (authLoading) return;
    
    if (isAuthenticated === false || (user && user.role !== 'admin')) {
      // Redirect non-admin users away from admin dashboard
      // Save current location so we can redirect back after login
      navigate('/login', { 
        state: { from: { pathname: '/admin' } },
        replace: true 
      });
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  useEffect(() => {
    // Only fetch data if authenticated, user is loaded, and is admin
    if (!token || !isAuthenticated || user?.role !== 'admin') return;
    if (currentView === 'chapters') {
      fetchUnits();
      fetchModules();
      fetchChapters();
    } else if (currentView === 'deliverables') {
      fetchChapters();
      fetchDocumentTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, token, isAuthenticated, user]);

  // Handle panel resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const container = document.querySelector('.flex.flex-col.lg\\:flex-row');
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const totalWidth = containerRect.width;
      const percentage = (mouseX / totalWidth) * 100;

      if (isResizing === 'unit-module') {
        const minWidth = 15; // Minimum 15% width
        const maxWidth = 50; // Maximum 50% width
        const newUnitWidth = Math.max(minWidth, Math.min(maxWidth, percentage));
        const remaining = 100 - newUnitWidth;
        const moduleWidth = remaining / 2;
        const chapterWidth = remaining / 2;
        
        setPanelWidths({
          unit: newUnitWidth,
          module: moduleWidth,
          chapter: chapterWidth
        });
      } else if (isResizing === 'module-chapter') {
        const unitWidth = panelWidths.unit;
        const remaining = 100 - unitWidth;
        const mouseXRelative = mouseX - (containerRect.width * unitWidth / 100);
        const moduleWidth = Math.max(15, Math.min(50, (mouseXRelative / remaining) * 100));
        const chapterWidth = 100 - unitWidth - moduleWidth;
        
        setPanelWidths({
          unit: unitWidth,
          module: moduleWidth,
          chapter: Math.max(15, chapterWidth)
        });
      }
    };

    const handleMouseUp = () => {
      setIsResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, panelWidths]);

  const fetchUnits = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/units`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication expired. Please log in again.');
          return;
        }
        throw new Error('Failed to fetch units.');
      }
      const data = await response.json();
      setUnits(data.sort((a, b) => a.order - b.order));
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while fetching units.');
    }
  };

  const fetchModules = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/modules`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication expired. Please log in again.');
          return;
        }
        throw new Error('Failed to fetch modules.');
      }
      const data = await response.json();
      setModules(data.sort((a, b) => a.order - b.order));
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while fetching modules.');
    }
  };

  const fetchChapters = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication expired. Please log in again.');
          return;
        }
        throw new Error('Failed to fetch chapters.');
      }

      const data = await response.json();
      data.sort((a, b) => a.order - b.order);
      setChapters(data);
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while fetching chapters.');
    }
  };

  const fetchDocumentTemplates = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/document-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch document templates.');
      const data = await response.json();
      setDocumentTemplates(data);
    } catch (err) {
      logger.error('Error fetching document templates:', err);
      setError(err.message || 'An error occurred.');
    }
  };

  const handleOpenDocumentTemplateModal = (template = null) => {
    if (template) {
      setEditingDocumentTemplate(template);
      const adminInputs = template.adminInputs && typeof template.adminInputs === 'object' ? template.adminInputs : {};
      setDocumentTemplateForm({
        name: template.name,
        description: template.description || '',
        documentType: template.documentType || 'pdf',
        requiredChapterIds: (template.requiredChapterIds || []).map(c => c._id || c),
        adminInputs,
        adminInputsRaw: JSON.stringify(adminInputs, null, 2),
        aiInstructions: template.aiInstructions || '',
        outputStructure: template.outputStructure,
        isActive: template.isActive !== false,
        order: template.order ?? 0
      });
    } else {
      setEditingDocumentTemplate(null);
      setDocumentTemplateForm({
        name: '',
        description: '',
        documentType: 'pdf',
        requiredChapterIds: [],
        adminInputs: {},
        adminInputsRaw: '',
        aiInstructions: '',
        outputStructure: null,
        isActive: true,
        order: documentTemplates.length
      });
    }
    setShowDocumentTemplateModal(true);
  };

  /** Chapters sorted by unit order → module order → chapter order for document template dropdown */
  const chaptersSortedForTemplates = React.useMemo(() => {
    if (!chapters || chapters.length === 0) return [];
    return [...chapters].sort((a, b) => {
      const unitOrderA = a.moduleId?.unitId?.order ?? 999;
      const unitOrderB = b.moduleId?.unitId?.order ?? 999;
      if (unitOrderA !== unitOrderB) return unitOrderA - unitOrderB;
      const modOrderA = a.moduleId?.order ?? 999;
      const modOrderB = b.moduleId?.order ?? 999;
      if (modOrderA !== modOrderB) return modOrderA - modOrderB;
      return (a.order ?? 999) - (b.order ?? 999);
    });
  }, [chapters]);

  const handleSaveDocumentTemplate = async (e) => {
    e.preventDefault();
    if (!documentTemplateForm.name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      setError('');
      const url = editingDocumentTemplate
        ? `${import.meta.env.VITE_API_URL}/api/admin/document-templates/${editingDocumentTemplate._id}`
        : `${import.meta.env.VITE_API_URL}/api/admin/document-templates`;
      const method = editingDocumentTemplate ? 'PUT' : 'POST';
      let adminInputs = {};
      try {
        adminInputs = documentTemplateForm.adminInputsRaw ? JSON.parse(documentTemplateForm.adminInputsRaw) : {};
      } catch {
        setError('Admin inputs must be valid JSON.');
        return;
      }
      const body = {
        ...documentTemplateForm,
        requiredChapterIds: Array.isArray(documentTemplateForm.requiredChapterIds) ? documentTemplateForm.requiredChapterIds : [],
        adminInputs
      };
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save.');
      }
      await fetchDocumentTemplates();
      setShowDocumentTemplateModal(false);
      setEditingDocumentTemplate(null);
    } catch (err) {
      setError(err.message || 'An error occurred.');
    }
  };

  const requestDeleteDocumentTemplate = (tpl) => {
    setError('');
    setDeleteConfirmation({
      kind: 'documentTemplate',
      id: tpl._id,
      name: tpl.name || 'this template',
    });
  };

  const handleOpenCreateModal = (itemType) => {
    setEditingItemType(itemType);
    
    if (itemType === 'unit') {
      const maxOrder = units.length > 0 ? Math.max(...units.map(u => u.order || 0)) : 0;
      setNewItemForm({
        name: '',
        title: '',
        description: '',
        moduleId: '',
        unitId: '',
        order: (maxOrder + 1).toString(),
        isRequired: true
      });
    } else if (itemType === 'module') {
      if (!selectedUnit) {
        setError('Please select a unit first.');
        return;
      }
      const modulesInUnit = modules.filter(m => (m.unitId?._id || m.unitId) === selectedUnit._id);
      const maxOrder = modulesInUnit.length > 0 ? Math.max(...modulesInUnit.map(m => m.order || 0)) : 0;
      setNewItemForm({
        name: '',
        title: '',
        description: '',
        moduleId: '',
        unitId: selectedUnit._id,
        order: (maxOrder + 1).toString(),
        isRequired: true,
        color: 'gray'
      });
    } else if (itemType === 'chapter') {
      if (!selectedModule) {
        setError('Please select a module first.');
        return;
      }
      const chaptersInModule = chapters.filter(c => (c.moduleId?._id || c.moduleId) === selectedModule._id);
      const maxOrder = chaptersInModule.length > 0 ? Math.max(...chaptersInModule.map(c => c.order || 0)) : 0;
      setNewItemForm({
        name: '',
        title: '',
        description: '',
        moduleId: selectedModule._id,
        unitId: '',
        order: (maxOrder + 1).toString(),
        isRequired: true
      });
    }
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setEditingItemType(null);
    setNewItemForm({
      name: '',
      title: '',
      description: '',
      moduleId: '',
      unitId: '',
      order: '',
      isRequired: true
    });
    setError('');
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }
    const itemType = editingItemType;

    try {
      if (itemType === 'unit') {
        if (!newItemForm.name.trim()) {
          setError('Unit name is required.');
          return;
        }
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/units`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: newItemForm.name.trim(),
            description: newItemForm.description.trim() || '',
            order: parseInt(newItemForm.order) || 0
          }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create unit.');
        }
        await fetchUnits();
        const newUnit = await response.json();
        setSelectedUnit(newUnit);
        setSelectedModule(null);
        setSelectedChapter(null);
      } else if (itemType === 'module') {
        if (!newItemForm.name.trim()) {
          setError('Module name is required.');
          return;
        }
        if (!newItemForm.unitId) {
          setError('Unit is required.');
          return;
        }
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/modules`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            unitId: newItemForm.unitId,
            name: newItemForm.name.trim(),
            description: newItemForm.description.trim() || '',
            order: parseInt(newItemForm.order) || 0,
            color: newItemForm.color || 'gray'
          }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create module.');
        }
        await fetchModules();
        const newModule = await response.json();
        setSelectedModule(newModule);
        setSelectedChapter(null);
      } else if (itemType === 'chapter') {
        if (!newItemForm.title.trim()) {
          setError('Chapter title is required.');
          return;
        }
        if (!newItemForm.moduleId) {
          setError('Module is required.');
          return;
        }
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
          body: JSON.stringify({ 
            moduleId: newItemForm.moduleId,
            order: parseInt(newItemForm.order) || 0,
            title: newItemForm.title.trim(), 
            description: newItemForm.description.trim() || '',
            isRequired: newItemForm.isRequired,
            contentBlocks: [], 
            llmConfig: { feedbackPrompt: 'Provide constructive feedback on the user\'s answers.' }
          }),
        });
      if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create chapter.');
      }
        await fetchChapters();
      const newChapter = await response.json();
        setSelectedChapter(newChapter);
      setSelectedBlock(null);
      }
      handleCloseCreateModal();
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || `An error occurred while creating the ${itemType}.`);
    }
  };

  const updateChapter = async (chapterId, updatedData) => {
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters/${chapterId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication expired. Please log in again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to update chapter.');
      }

      const updatedChapter = await response.json();
      // Update and re-sort by order
      const updatedChapters = chapters.map(ch => (ch._id === chapterId ? updatedChapter : ch)).sort((a, b) => a.order - b.order);
      setChapters(updatedChapters);

      // If the currently selected chapter was updated, update the state
      if (selectedChapter && selectedChapter._id === chapterId) {
        setSelectedChapter(updatedChapter);
      }
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while updating the module.');
    }
  };

  const handleStartEdit = (item, itemType) => {
    setEditingItem(item);
    setEditingItemType(itemType);
    
    if (itemType === 'unit') {
      setEditItemForm({
        name: item.name || '',
        title: '',
        description: item.description || '',
        moduleId: '',
        unitId: '',
        order: item.order?.toString() || '',
        isRequired: true
      });
    } else if (itemType === 'module') {
      setEditItemForm({
        name: item.name || '',
        title: '',
        description: item.description || '',
        moduleId: '',
        unitId: item.unitId?._id || item.unitId || '',
        order: item.order?.toString() || '',
        isRequired: true,
        color: item.color || 'gray'
      });
    } else if (itemType === 'chapter') {
      setEditItemForm({
        name: '',
        title: item.title || '',
        description: item.description || '',
        moduleId: item.moduleId?._id || item.moduleId || '',
        unitId: '',
        order: item.order?.toString() || '',
        isRequired: item.isRequired !== false
      });
    }
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingItem(null);
    setEditingItemType(null);
    setEditItemForm({
      name: '',
      title: '',
      description: '',
      moduleId: '',
      unitId: '',
      order: '',
      isRequired: true
    });
    setError('');
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }
    const itemType = editingItemType;

    try {
      if (itemType === 'unit') {
        if (!editItemForm.name.trim()) {
          setError('Unit name is required.');
      return;
    }
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/units/${editingItem._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: editItemForm.name.trim(),
            description: editItemForm.description.trim() || '',
            order: parseInt(editItemForm.order) || 0
          }),
        });
        if (!response.ok) throw new Error('Failed to update unit.');
        await fetchUnits();
      } else if (itemType === 'module') {
        if (!editItemForm.name.trim()) {
          setError('Module name is required.');
          return;
        }
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/modules/${editingItem._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            unitId: editItemForm.unitId,
            name: editItemForm.name.trim(),
            description: editItemForm.description.trim() || '',
            order: parseInt(editItemForm.order) || 0,
            color: editItemForm.color || 'gray'
          }),
        });
        if (!response.ok) throw new Error('Failed to update module.');
        await fetchModules();
      } else if (itemType === 'chapter') {
        if (!editItemForm.title.trim()) {
          setError('Chapter title is required.');
          return;
        }
        if (!editItemForm.moduleId) {
          setError('Module is required.');
          return;
        }
        await updateChapter(editingItem._id, {
          title: editItemForm.title.trim(),
          description: editItemForm.description.trim() || '',
          moduleId: editItemForm.moduleId,
          order: parseInt(editItemForm.order) || 0,
          isRequired: editItemForm.isRequired
        });
        await fetchChapters();
      }
      handleCloseEditModal();
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || `An error occurred while updating the ${itemType}.`);
    }
  };

  const handleFeedbackPromptUpdate = async (newPrompt) => {
    if (!selectedChapter) return;
    try {
      // Use the generic updateChapter function with new llmConfig structure
      await updateChapter(selectedChapter._id, { 
        llmConfig: { 
          ...(selectedChapter.llmConfig || {}),
          feedbackPrompt: newPrompt 
        } 
      });
      // No need to manually update state here, updateChapter handles it
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while updating the feedback prompt.');
    }
  };

  const handleDelete = (item, itemType, event) => {
    if (event) event.stopPropagation();
    setError('');
    setDeleteConfirmation({ kind: itemType, item });
  };

  const confirmPendingDelete = async () => {
    if (!deleteConfirmation || !token) return;
    const d = deleteConfirmation;
    setDeleteInProgress(true);
    setError('');
    try {
      if (d.kind === 'unit' || d.kind === 'module' || d.kind === 'chapter') {
        const item = d.item;
        const itemType = d.kind;
        let url;
        if (itemType === 'unit') {
          url = `${import.meta.env.VITE_API_URL}/api/units/${item._id}`;
        } else if (itemType === 'module') {
          url = `${import.meta.env.VITE_API_URL}/api/modules/${item._id}`;
        } else {
          url = `${import.meta.env.VITE_API_URL}/api/admin/chapters/${item._id}`;
        }
        const response = await fetch(url, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to delete ${itemType}.`);
        }
        if (itemType === 'unit') {
          await fetchUnits();
          if (selectedUnit && selectedUnit._id === item._id) {
            setSelectedUnit(null);
            setSelectedModule(null);
            setSelectedChapter(null);
          }
        } else if (itemType === 'module') {
          await fetchModules();
          if (selectedModule && selectedModule._id === item._id) {
            setSelectedModule(null);
            setSelectedChapter(null);
          }
        } else {
          await fetchChapters();
          if (selectedChapter && selectedChapter._id === item._id) {
            setSelectedChapter(null);
            setSelectedBlock(null);
          }
        }
      } else if (d.kind === 'block') {
        if (!selectedChapter || d.blockIndex === undefined) return;
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/admin/chapters/${selectedChapter._id}/contentblocks/${d.blockIndex}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to delete content block.');
        }
        const result = await response.json();
        const updatedChapters = chapters
          .map((ch) => (ch._id === result.chapter._id ? result.chapter : ch))
          .sort((a, b) => a.order - b.order);
        setChapters(updatedChapters);
        setSelectedChapter(result.chapter);
        setSelectedBlock(null);
      } else if (d.kind === 'documentTemplate') {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/admin/document-templates/${d.id}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) throw new Error('Failed to delete.');
        await fetchDocumentTemplates();
      } else if (d.kind === 'promptTemplate') {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts/${d.template._id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to delete template');
        }
        setSelectedTemplate(null);
        setTemplateRefreshKey((prev) => prev + 1);
      }
    } catch (err) {
      logger.error('Delete confirmation error in AdminDashboard:', err);
      setError(err.message || `Delete failed for ${itemType}.`);
    } finally {
      setDeleteInProgress(false);
      setDeleteConfirmation(null);
    }
  };

  const getDeleteModalCopy = () => {
    const d = deleteConfirmation;
    if (!d) return { title: '', body: '' };
    if (d.kind === 'unit') {
      return {
        title: 'Delete unit?',
        body: `"${d.item.name || 'This unit'}" and all modules and chapters under it will be permanently removed. This cannot be undone.`,
      };
    }
    if (d.kind === 'module') {
      return {
        title: 'Delete module?',
        body: `"${d.item.name || 'This module'}" and all its chapters will be permanently removed. This cannot be undone.`,
      };
    }
    if (d.kind === 'chapter') {
      return {
        title: 'Delete chapter?',
        body: `"${d.item.title || d.item.name || 'This chapter'}" and all of its content blocks will be permanently removed. This cannot be undone.`,
      };
    }
    if (d.kind === 'block') {
      return {
        title: 'Delete content block?',
        body: 'This block will be removed from the chapter. This cannot be undone.',
      };
    }
    if (d.kind === 'documentTemplate') {
      return {
        title: 'Delete document template?',
        body: `Remove "${d.name}"? Users will no longer be able to generate this deliverable. This cannot be undone.`,
      };
    }
    if (d.kind === 'promptTemplate') {
      return {
        title: 'Delete prompt template?',
        body: `Remove "${d.template.name || 'this template'}"? This action cannot be undone.`,
      };
    }
    return { title: 'Delete?', body: 'This cannot be undone.' };
  };

  const selectUnit = (unit) => {
    setSelectedUnit(unit);
    setSelectedModule(null);
    setSelectedChapter(null);
    setSelectedBlock(null);
  };

  const handleUnitDragStart = (e, unit) => {
    setDraggedUnit(unit);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleUnitDragOver = (e, unitIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(unitIndex);
    setDragOverType('unit');
  };

  const handleUnitDrop = async (e, targetUnit) => {
    e.preventDefault();
    if (!token) {
      setError('Authentication required. Please log in again.');
      setDraggedUnit(null);
      return;
    }
    if (!draggedUnit || draggedUnit._id === targetUnit._id) {
      setDraggedUnit(null);
      return;
    }

    const draggedIndex = units.findIndex(u => u._id === draggedUnit._id);
    const targetIndex = units.findIndex(u => u._id === targetUnit._id);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedUnit(null);
      return;
    }

    // Reorder units array
    const reorderedUnits = [...units];
    const [removed] = reorderedUnits.splice(draggedIndex, 1);
    reorderedUnits.splice(targetIndex, 0, removed);

    // Update orders
    const unitOrders = reorderedUnits.map((unit, index) => ({
      unitId: unit._id,
      order: index
    }));

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/units/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ unitOrders }),
      });

      if (!response.ok) {
        throw new Error('Failed to reorder units.');
      }

      // Update local state with new orders
      const updatedUnits = reorderedUnits.map((unit, index) => ({
        ...unit,
        order: index
      }));
      setUnits(updatedUnits);
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while reordering units.');
    }

    setDraggedUnit(null);
  };

  const handleUnitDragEnd = () => {
    setDraggedUnit(null);
    setDragOverIndex(null);
    setDragOverType(null);
  };

  const moveUnit = async (unitIndex, direction) => {
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }
    if (unitIndex < 0 || unitIndex >= units.length) return;
    const newIndex = direction === 'up' ? unitIndex - 1 : unitIndex + 1;
    if (newIndex < 0 || newIndex >= units.length) return;

    const reorderedUnits = [...units];
    const [removed] = reorderedUnits.splice(unitIndex, 1);
    reorderedUnits.splice(newIndex, 0, removed);

    const unitOrders = reorderedUnits.map((unit, index) => ({
      unitId: unit._id.toString(),
      order: index
    }));

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/units/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ unitOrders }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication expired. Please log in again.');
          // Optionally redirect to login
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }
        let errorMessage = 'Failed to reorder units.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        console.error('Reorder units error:', errorMessage, 'Response status:', response.status);
        throw new Error(errorMessage);
      }

      const updatedUnits = reorderedUnits.map((unit, index) => ({
        ...unit,
        order: index
      }));
      setUnits(updatedUnits);
      
      // Visual feedback
      setMovedItemId(removed._id);
      setMovedItemType('unit');
      setTimeout(() => {
        setMovedItemId(null);
        setMovedItemType(null);
      }, 2000);
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while reordering units.');
    }
  };

  const moveModule = async (moduleIndex, direction) => {
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }
    if (!selectedUnit) return;
    const modulesInUnit = modules.filter(m => {
      const moduleUnitId = m.unitId?._id?.toString() || m.unitId?.toString() || (m.unitId && m.unitId.toString ? m.unitId.toString() : m.unitId);
      const selectedUnitId = selectedUnit._id?.toString() || selectedUnit._id;
      return moduleUnitId === selectedUnitId;
    });

    if (moduleIndex < 0 || moduleIndex >= modulesInUnit.length) return;
    const newIndex = direction === 'up' ? moduleIndex - 1 : moduleIndex + 1;
    if (newIndex < 0 || newIndex >= modulesInUnit.length) return;

    const reorderedModules = [...modulesInUnit];
    const [removed] = reorderedModules.splice(moduleIndex, 1);
    reorderedModules.splice(newIndex, 0, removed);

    try {
      const newOrder = newIndex;
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/modules/${removed._id}/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ newOrder }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication expired. Please log in again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to reorder modules.');
      }
      await fetchModules();
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while reordering modules.');
    }
  };

  const moveChapter = async (chapterIndex, direction) => {
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }
    if (!selectedModule) return;
    const chaptersInModule = chapters.filter(c => {
      const chapterModuleId = c.moduleId?._id?.toString() || c.moduleId?.toString() || (c.moduleId && c.moduleId.toString ? c.moduleId.toString() : c.moduleId);
      const selectedModuleId = selectedModule._id?.toString() || selectedModule._id;
      return chapterModuleId === selectedModuleId;
    });

    if (chapterIndex < 0 || chapterIndex >= chaptersInModule.length) return;
    const newIndex = direction === 'up' ? chapterIndex - 1 : chapterIndex + 1;
    if (newIndex < 0 || newIndex >= chaptersInModule.length) return;

    const reorderedChapters = [...chaptersInModule];
    const [removed] = reorderedChapters.splice(chapterIndex, 1);
    reorderedChapters.splice(newIndex, 0, removed);

    try {
      // First, set all chapters to temporary negative orders to avoid unique constraint violations
      const tempOffset = -10000;
      const tempUpdatePromises = reorderedChapters.map((chapter, index) =>
        updateChapter(chapter._id, { order: tempOffset - index })
      );
      await Promise.all(tempUpdatePromises);
      
      // Then, update them to their final order values sequentially
      for (let i = 0; i < reorderedChapters.length; i++) {
        await updateChapter(reorderedChapters[i]._id, { order: i });
      }
      
      await fetchChapters();
      
      // Visual feedback
      setMovedItemId(removed._id);
      setMovedItemType('chapter');
      setTimeout(() => {
        setMovedItemId(null);
        setMovedItemType(null);
      }, 2000);
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while reordering chapters.');
    }
  };

  const selectModule = (module) => {
    setSelectedModule(module);
    setSelectedChapter(null);
    setSelectedBlock(null);
  };

  const handleModuleDragStart = (e, module) => {
    setDraggedModule(module);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleModuleDragOver = (e, moduleIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(moduleIndex);
    setDragOverType('module');
  };

  const handleModuleDrop = async (e, targetModule) => {
    e.preventDefault();
    if (!token) {
      setError('Authentication required. Please log in again.');
      setDraggedModule(null);
      return;
    }
    if (!draggedModule || !selectedUnit || draggedModule._id === targetModule._id) {
      setDraggedModule(null);
      return;
    }

    // Get modules in the selected unit
    const modulesInUnit = modules.filter(m => {
      const moduleUnitId = m.unitId?._id?.toString() || m.unitId?.toString() || (m.unitId && m.unitId.toString ? m.unitId.toString() : m.unitId);
      const selectedUnitId = selectedUnit._id?.toString() || selectedUnit._id;
      return moduleUnitId === selectedUnitId;
    });

    const draggedIndex = modulesInUnit.findIndex(m => m._id === draggedModule._id);
    const targetIndex = modulesInUnit.findIndex(m => m._id === targetModule._id);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedModule(null);
      return;
    }

    // Reorder modules array
    const reorderedModules = [...modulesInUnit];
    const [removed] = reorderedModules.splice(draggedIndex, 1);
    reorderedModules.splice(targetIndex, 0, removed);

    // Update orders using the API
    try {
      const newOrder = targetIndex;
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/modules/${draggedModule._id}/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ newOrder }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication expired. Please log in again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to reorder modules.');
      }

      // Refetch modules to get updated order
      await fetchModules();
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while reordering modules.');
    }

    setDraggedModule(null);
  };

  const handleModuleDragEnd = () => {
    setDraggedModule(null);
    setDragOverIndex(null);
    setDragOverType(null);
  };

  const handleChapterDragStart = (e, chapter) => {
    setDraggedChapter(chapter);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleChapterDragOver = (e, chapterIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(chapterIndex);
    setDragOverType('chapter');
  };

  const handleChapterDrop = async (e, targetChapter) => {
    e.preventDefault();
    if (!token) {
      setError('Authentication required. Please log in again.');
      setDraggedChapter(null);
      return;
    }
    if (!draggedChapter || !selectedModule || draggedChapter._id === targetChapter._id) {
      setDraggedChapter(null);
      return;
    }

    // Get chapters in the selected module
    const chaptersInModule = chapters.filter(c => {
      const chapterModuleId = c.moduleId?._id?.toString() || c.moduleId?.toString() || (c.moduleId && c.moduleId.toString ? c.moduleId.toString() : c.moduleId);
      const selectedModuleId = selectedModule._id?.toString() || selectedModule._id;
      return chapterModuleId === selectedModuleId;
    });

    const draggedIndex = chaptersInModule.findIndex(c => c._id === draggedChapter._id);
    const targetIndex = chaptersInModule.findIndex(c => c._id === targetChapter._id);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedChapter(null);
      return;
    }

    // Reorder chapters array
    const reorderedChapters = [...chaptersInModule];
    const [removed] = reorderedChapters.splice(draggedIndex, 1);
    reorderedChapters.splice(targetIndex, 0, removed);

    // Update orders using the updateChapter API
    try {
      // First, set all chapters to temporary negative orders to avoid unique constraint violations
      const tempOffset = -10000;
      const tempUpdatePromises = reorderedChapters.map((chapter, index) =>
        updateChapter(chapter._id, { order: tempOffset - index })
      );
      await Promise.all(tempUpdatePromises);
      
      // Then, update them to their final order values sequentially
      for (let i = 0; i < reorderedChapters.length; i++) {
        await updateChapter(reorderedChapters[i]._id, { order: i });
      }
      
      await fetchChapters();
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while reordering chapters.');
    }

    setDraggedChapter(null);
  };

  const handleChapterDragEnd = () => {
    setDraggedChapter(null);
    setDragOverIndex(null);
    setDragOverType(null);
  };

  const selectChapter = async (chapter) => {
    // Fetch the full chapter data to ensure we have contentBlocks
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const allChapters = await response.json();
        const fullChapter = allChapters.find(ch => ch._id === chapter._id);
        if (fullChapter) {
          setSelectedChapter({
            ...fullChapter,
            contentBlocks: fullChapter.contentBlocks || []
          });
        } else {
          setSelectedChapter({
            ...chapter,
            contentBlocks: chapter.contentBlocks || []
          });
        }
      } else {
        setSelectedChapter({
          ...chapter,
          contentBlocks: chapter.contentBlocks || []
        });
      }
    } catch (err) {
      console.error('Error fetching full chapter data:', err);
      setSelectedChapter({
        ...chapter,
        contentBlocks: chapter.contentBlocks || []
      });
    }
    
    setSelectedBlock(null); // Deselect block when changing chapter
  };

  const selectBlock = (block) => {
    setSelectedBlock(block);
  };

  const moveBlock = async (fromIndex, toIndex) => {
    if (!selectedChapter || fromIndex < 0 || toIndex < 0 || fromIndex >= selectedChapter.contentBlocks.length || toIndex >= selectedChapter.contentBlocks.length) {
      return;
    }

    const updatedBlocks = [...selectedChapter.contentBlocks];
    const [blockToMove] = updatedBlocks.splice(fromIndex, 1);
    updatedBlocks.splice(toIndex, 0, blockToMove);

    // Update block indices to match their new positions
    updatedBlocks.forEach((block, idx) => {
      block.index = idx;
    });

    // Use the generic updateChapter function
    try {
      await updateChapter(selectedChapter._id, { contentBlocks: updatedBlocks });
      // updateChapter handles updating selectedChapter state if necessary
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while reordering the content blocks.');
    }
  };

  const handleAddBlock = async (blockType = 'text') => {
    if (!selectedChapter) return;

    let newBlockDataPayload;

    // Get the next index for the new block
    const nextIndex = selectedChapter.contentBlocks.length > 0 
      ? Math.max(...selectedChapter.contentBlocks.map(b => b.index || 0)) + 1 
      : 0;

    switch (blockType) {
        case 'text':
            newBlockDataPayload = { 
              index: nextIndex,
              blockType: 'text', 
              content: { text: '<p>New Text Content</p>' },
              metadata: {}
            };
            break;
        case 'image':
            newBlockDataPayload = { 
              index: nextIndex,
              blockType: 'image', 
              content: { media: { url: '' } },
              metadata: {}
            };
            break;
        case 'question':
        case 'multi_choice':
            newBlockDataPayload = { 
              index: nextIndex,
              blockType: blockType === 'question' ? 'question' : 'multi_choice', 
              content: { 
                prompt: 'New Question Prompt', 
                questionType: 'short_answer',
                options: [] 
              },
              metadata: {}
            };
            break;
        case 'exercise': // Tables are now exercise blocks
            newBlockDataPayload = {
              index: nextIndex,
              blockType: 'exercise',
              content: {
                    headings: ['Heading 1'],
                    rows: [['']],
                    frozenCells: {},
                    autoFillCells: {},
                    tooltips: {},
                    fixedColumns: {},
                    textContent: '',
                    selectedColumn: null,
                    autoFillPrompt: 'Default prompt: This table is about [topic]. Given the user summary ({{USER_SUMMARY}}) and the current table structure ({{TABLE}}), please fill in the empty cells that are marked for auto-fill.'
              },
              metadata: {}
            };
            break;
        case 'ai_chat':
            newBlockDataPayload = {
              index: nextIndex,
              blockType: 'ai_chat',
              content: {
                    systemPrompt: 'You are a helpful assistant who helps users with creative projects.',
                welcomeMessage: 'Hello! How can I assist you today?',
              },
              metadata: {}
            };
            break;
        default:
            newBlockDataPayload = { 
              index: nextIndex,
              blockType: blockType, 
              content: {},
              metadata: {}
            };
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters/${selectedChapter._id}/contentblocks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newBlockDataPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add content block.');
      }

      const updatedChapter = await response.json();
      // Update and re-sort chapters by order
      const updatedChapters = chapters.map(ch => (ch._id === updatedChapter._id ? updatedChapter : ch)).sort((a, b) => a.order - b.order);
      setChapters(updatedChapters);
      setSelectedChapter(updatedChapter); // Update selected chapter with new block list
      // Optionally select the newly added block
      setSelectedBlock(updatedChapter.contentBlocks[updatedChapter.contentBlocks.length - 1]);
    } catch (err) {
      logger.error('Error in AdminDashboard:', err);
      setError(err.message || 'An error occurred while adding the content block.');
    }
  };

  const handleDeleteBlock = (blockIndex) => {
    if (!selectedChapter || blockIndex === undefined) return;
    setError('');
    setDeleteConfirmation({ kind: 'block', blockIndex });
  };

  // This function updates the block locally after it has been saved via the single-block endpoint.
  // The actual persistence is handled by ContentBlockEditor.handleSave(), so we only update local state here.
  const handleUpdateBlock = (updatedBlockData) => {
    if (!selectedChapter || !selectedBlock) return;

    // Create the locally updated chapter object using the updated block data from the API response
    const updatedChapterLocally = {
      ...selectedChapter,
      contentBlocks: selectedChapter.contentBlocks.map(block =>
        block.index === selectedBlock.index ? { ...block, ...updatedBlockData } : block
      ),
    };

    // Update the state immediately for UI responsiveness
    // Note: The block has already been saved to the backend via ContentBlockEditor.handleSave()
    // so we only need to update local state here, not call updateChapter again
    setChapters(chapters.map(ch => (ch._id === updatedChapterLocally._id ? updatedChapterLocally : ch)));
    setSelectedChapter(updatedChapterLocally);
    setSelectedBlock({ ...selectedBlock, ...updatedBlockData }); // Update selected block state as well
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-4 md:p-8 font-body">

      {/* View Navigation */}
      <div className="mb-6 flex items-center justify-between border-b border-gray-300 dark:border-gray-700">
        <div className="flex space-x-2">
        <button
          onClick={() => setCurrentView('chapters')}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            currentView === 'chapters'
                ? 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 border-b-0 text-black dark:text-white'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
            <BookOpenIcon className="h-5 w-5 mr-2" /> Makerstate Editor
        </button>
        <button
          onClick={() => setCurrentView('users')}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            currentView === 'users'
                ? 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 border-b-0 text-black dark:text-white'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <UsersIcon className="h-5 w-5 mr-2" /> User Management
        </button>
        <button
          onClick={() => setCurrentView('usage')}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            currentView === 'usage'
                ? 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 border-b-0 text-black dark:text-white'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <ChartBarIcon className="h-5 w-5 mr-2" /> AI Usage Stats
        </button>
        <button
          onClick={() => setCurrentView('prompts')}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            currentView === 'prompts'
                ? 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 border-b-0 text-black dark:text-white'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <DocumentTextIcon className="h-5 w-5 mr-2" /> Prompt Templates
        </button>
        <button
          onClick={() => setCurrentView('deliverables')}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            currentView === 'deliverables'
                ? 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 border-b-0 text-black dark:text-white'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <DocumentDuplicateIcon className="h-5 w-5 mr-2" /> Document Templates
        </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 dark:bg-red-900 dark:border-red-700 dark:text-red-300" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
          <button onClick={() => setError('')} className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <span className="text-red-500 text-xl dark:text-red-300">×</span>
          </button>
        </div>
      )}

      {/* Conditional View Rendering */}
      {currentView === 'chapters' && (
        <div className="flex flex-col lg:flex-row lg:space-x-1 relative">
          {/* Panel 1: Units */}
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 flex flex-col max-h-[40vh] relative"
            style={{ width: `${panelWidths.unit}%` }}
          >
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-heading dark:text-white">Units</h2>
              <button
                onClick={() => handleOpenCreateModal('unit')}
                title="Add New Unit"
                className="flex items-center bg-black text-white px-2.5 py-1.5 rounded transition text-sm hover:bg-gray-700 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-600"
              >
                <PlusIcon className="h-4 w-4 mr-1" /> Add
              </button>
            </div>
            <div className="relative flex-1 overflow-hidden">
              <ul className="space-y-2 overflow-y-auto pr-1 h-full scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                {units.map((unit, unitIndex) => {
                const moduleCount = modules.filter(m => {
                  const moduleUnitId = m.unitId?._id?.toString() || m.unitId?.toString() || (m.unitId && m.unitId.toString ? m.unitId.toString() : m.unitId);
                  const unitId = unit._id?.toString() || unit._id;
                  return moduleUnitId === unitId;
                }).length;
                return (
                  <React.Fragment key={unit._id}>
                    {draggedUnit && dragOverType === 'unit' && dragOverIndex === unitIndex && draggedUnit._id !== unit._id && (
                      <li className="h-1 bg-blue-500 dark:bg-blue-400 rounded-full mx-2 my-1"></li>
                    )}
                    <li
                      draggable
                      onDragStart={(e) => handleUnitDragStart(e, unit)}
                      onDragOver={(e) => handleUnitDragOver(e, unitIndex)}
                      onDrop={(e) => handleUnitDrop(e, unit)}
                      onDragEnd={handleUnitDragEnd}
                      className={`p-3 rounded-lg cursor-move group border transition-all ${
                        selectedUnit && selectedUnit._id === unit._id
                          ? 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 shadow-sm'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                      } ${draggedUnit && draggedUnit._id === unit._id ? 'opacity-50' : ''} ${
                        movedItemId === unit._id && movedItemType === 'unit' ? 'ring-2 ring-blue-500 dark:ring-blue-400 bg-blue-50 dark:bg-blue-900/30' : ''
                      }`}
                      onClick={() => selectUnit(unit)}
                    >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1 break-words">
                          {unit.name}
                        </div>
                        {unit.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-1">
                            {unit.description}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                          {moduleCount} {moduleCount === 1 ? 'module' : 'modules'}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        {unitIndex > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); moveUnit(unitIndex, 'up'); }}
                            title="Move Up"
                            className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-600 dark:text-blue-400"
                          >
                            <ArrowUpIcon className="h-4 w-4" />
                          </button>
                        )}
                        {unitIndex < units.length - 1 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); moveUnit(unitIndex, 'down'); }}
                            title="Move Down"
                            className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-600 dark:text-blue-400"
                          >
                            <ArrowDownIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleStartEdit(unit, 'unit'); }}
                          title="Edit Unit"
                          className="p-1 rounded hover:bg-yellow-200 dark:hover:bg-yellow-700 text-yellow-600 dark:text-yellow-400"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleDelete(unit, 'unit', e)}
                          title="Delete Unit"
                          className="p-1 rounded hover:bg-red-200 dark:hover:bg-red-700 text-red-500 dark:text-red-400"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                  </React.Fragment>
                );
              })}
                {units.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No units created yet.</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Click "Add" to create one.</p>
                  </div>
                )}
              </ul>
              {/* Scroll indicator - bottom fade */}
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none"></div>
            </div>
          </div>
          
          {/* Resize handle between Units and Modules */}
          <div
            className="w-1 bg-gray-300 dark:bg-gray-600 hover:bg-blue-500 dark:hover:bg-blue-400 cursor-col-resize transition-colors"
            onMouseDown={(e) => {
              setIsResizing('unit-module');
              e.preventDefault();
            }}
          ></div>

          {/* Panel 2: Modules */}
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 flex flex-col max-h-[40vh] relative"
            style={{ width: `${panelWidths.module}%` }}
          >
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-heading dark:text-white">Modules</h2>
              <button
                onClick={() => handleOpenCreateModal('module')}
                title="Add New Module"
                className="flex items-center bg-black text-white px-2.5 py-1.5 rounded transition text-sm hover:bg-gray-700 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-600"
                disabled={!selectedUnit}
              >
                <PlusIcon className="h-4 w-4 mr-1" /> Add
              </button>
            </div>
            <div className="relative flex-1 overflow-hidden">
              <ul className="space-y-2 overflow-y-auto pr-1 h-full scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                {selectedUnit ? (
                  (() => {
                    const modulesInUnit = modules.filter(m => {
                      const moduleUnitId = m.unitId?._id?.toString() || m.unitId?.toString() || (m.unitId && m.unitId.toString ? m.unitId.toString() : m.unitId);
                      const selectedUnitId = selectedUnit._id?.toString() || selectedUnit._id?.toString() || selectedUnit._id;
                      return moduleUnitId === selectedUnitId;
                    });
                    return modulesInUnit.map((module, moduleIndex) => {
                  const chapterCount = chapters.filter(c => {
                    const chapterModuleId = c.moduleId?._id?.toString() || c.moduleId?.toString() || (c.moduleId && c.moduleId.toString ? c.moduleId.toString() : c.moduleId);
                    const moduleId = module._id?.toString() || module._id;
                    return chapterModuleId === moduleId;
                  }).length;
                  return (
                    <React.Fragment key={module._id}>
                      {draggedModule && dragOverType === 'module' && dragOverIndex === moduleIndex && draggedModule._id !== module._id && (
                        <li className="h-1 bg-blue-500 dark:bg-blue-400 rounded-full mx-2 my-1"></li>
                      )}
                      <li
                        draggable
                        onDragStart={(e) => handleModuleDragStart(e, module)}
                        onDragOver={(e) => handleModuleDragOver(e, moduleIndex)}
                        onDrop={(e) => handleModuleDrop(e, module)}
                        onDragEnd={handleModuleDragEnd}
                        className={`p-3 rounded-lg cursor-move group border transition-all ${
                          selectedModule && selectedModule._id === module._id
                            ? 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 shadow-sm'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                        } ${draggedModule && draggedModule._id === module._id ? 'opacity-50' : ''} ${
                          movedItemId === module._id && movedItemType === 'module' ? 'ring-2 ring-blue-500 dark:ring-blue-400 bg-blue-50 dark:bg-blue-900/30' : ''
                        }`}
                        onClick={() => selectModule(module)}
                      >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1 break-words">
                            {module.name}
                          </div>
                          {module.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-1">
                              {module.description}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {chapterCount} {chapterCount === 1 ? 'chapter' : 'chapters'}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          {moduleIndex > 0 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); moveModule(moduleIndex, 'up'); }}
                              title="Move Up"
                              className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-600 dark:text-blue-400"
                            >
                              <ArrowUpIcon className="h-4 w-4" />
                            </button>
                          )}
                          {moduleIndex < modulesInUnit.length - 1 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); moveModule(moduleIndex, 'down'); }}
                              title="Move Down"
                              className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-600 dark:text-blue-400"
                            >
                              <ArrowDownIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleStartEdit(module, 'module'); }}
                            title="Edit Module"
                            className="p-1 rounded hover:bg-yellow-200 dark:hover:bg-yellow-700 text-yellow-600 dark:text-yellow-400"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => handleDelete(module, 'module', e)}
                            title="Delete Module"
                            className="p-1 rounded hover:bg-red-200 dark:hover:bg-red-700 text-red-500 dark:text-red-400"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                      </div>
                    </div>
                    </li>
                    </React.Fragment>
                  );
                  });
                  })()
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Select a unit to view modules.</p>
                  </div>
                )}
              </ul>
              {/* Scroll indicator - bottom fade */}
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none"></div>
            </div>
          </div>
          
          {/* Resize handle between Modules and Chapters */}
          <div
            className="w-1 bg-gray-300 dark:bg-gray-600 hover:bg-blue-500 dark:hover:bg-blue-400 cursor-col-resize transition-colors"
            onMouseDown={(e) => {
              setIsResizing('module-chapter');
              e.preventDefault();
            }}
          ></div>

          {/* Panel 3: Chapters */}
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 flex flex-col max-h-[40vh] relative"
            style={{ width: `${panelWidths.chapter}%` }}
          >
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-heading dark:text-white">Chapters</h2>
              <button
                onClick={() => handleOpenCreateModal('chapter')}
                title="Add New Chapter"
                className="flex items-center bg-black text-white px-2.5 py-1.5 rounded transition text-sm hover:bg-gray-700 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-600"
                disabled={!selectedModule}
              >
                <PlusIcon className="h-4 w-4 mr-1" /> Add
              </button>
            </div>
            <div className="relative flex-1 overflow-hidden">
              <ul className="space-y-2 overflow-y-auto pr-1 h-full scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                {selectedModule ? (
                  (() => {
                    const chaptersInModule = chapters.filter(c => {
                      const chapterModuleId = c.moduleId?._id?.toString() || c.moduleId?.toString() || (c.moduleId && c.moduleId.toString ? c.moduleId.toString() : c.moduleId);
                      const selectedModuleId = selectedModule._id?.toString() || selectedModule._id;
                      return chapterModuleId === selectedModuleId;
                    });
                    return chaptersInModule.map((chapter, chapterIndex) => (
                      <React.Fragment key={chapter._id}>
                        {draggedChapter && dragOverType === 'chapter' && dragOverIndex === chapterIndex && draggedChapter._id !== chapter._id && (
                          <li className="h-1 bg-blue-500 dark:bg-blue-400 rounded-full mx-2 my-1"></li>
                        )}
                        <li
                          draggable
                          onDragStart={(e) => handleChapterDragStart(e, chapter)}
                          onDragOver={(e) => handleChapterDragOver(e, chapterIndex)}
                          onDrop={(e) => handleChapterDrop(e, chapter)}
                          onDragEnd={handleChapterDragEnd}
                          className={`p-3 rounded-lg cursor-move group border transition-all ${
                            selectedChapter && selectedChapter._id === chapter._id
                              ? 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 shadow-sm'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                          } ${draggedChapter && draggedChapter._id === chapter._id ? 'opacity-50' : ''} ${
                            movedItemId === chapter._id && movedItemType === 'chapter' ? 'ring-2 ring-blue-500 dark:ring-blue-400 bg-blue-50 dark:bg-blue-900/30' : ''
                          }`}
                          onClick={() => selectChapter(chapter)}
                        >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          #{chapter.order}
                    </div>
                        <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1 break-words">
                          {chapter.title}
                        </div>
                        {chapter.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                            {chapter.description}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        {chapterIndex > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); moveChapter(chapterIndex, 'up'); }}
                            title="Move Up"
                            className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-600 dark:text-blue-400"
                          >
                            <ArrowUpIcon className="h-4 w-4" />
                          </button>
                        )}
                        {chapterIndex < chaptersInModule.length - 1 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); moveChapter(chapterIndex, 'down'); }}
                            title="Move Down"
                            className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-600 dark:text-blue-400"
                          >
                            <ArrowDownIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleStartEdit(chapter, 'chapter'); }}
                          title="Edit Chapter"
                          className="p-1 rounded hover:bg-yellow-200 dark:hover:bg-yellow-700 text-yellow-600 dark:text-yellow-400"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleDelete(chapter, 'chapter', e)}
                          title="Delete Chapter"
                          className="p-1 rounded hover:bg-red-200 dark:hover:bg-red-700 text-red-500 dark:text-red-400"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                  </React.Fragment>
                ));
                  })()
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Select a module to view chapters.</p>
                  </div>
                )}
              </ul>
              {/* Scroll indicator - bottom fade */}
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none"></div>
            </div>
          </div>
        </div>
      )}

      {/* Content Blocks and Editor Section - Below the three panels */}
      {currentView === 'chapters' && selectedChapter && (
        <div className="mt-6 flex flex-col lg:flex-row lg:space-x-4">
          {/* Column 1: Content Blocks List */}
          <div className="lg:w-1/4 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-heading dark:text-white">Content Blocks</h2>
                   <button
                      onClick={() => handleAddBlock('text')}
                      title="Add Text Block"
                className="flex items-center bg-black text-white px-2.5 py-1.5 rounded transition text-sm hover:bg-gray-700 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-600"
                    >
                <PlusIcon className="h-4 w-4 mr-1" /> Add
                    </button>
                </div>
            <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
              {selectedChapter.contentBlocks && selectedChapter.contentBlocks.length > 0 ? (
                selectedChapter.contentBlocks.map((block, index) => (
                  <li
                    key={block.index !== undefined ? block.index : index}
                    className={`p-3 rounded-lg cursor-pointer group border transition-all ${
                      selectedBlock && selectedBlock.index === block.index
                        ? 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 shadow-sm'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                      }`}
                      onClick={() => selectBlock(block)}
                    >
                      <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                        {index + 1}. {(block.blockType || 'unknown').charAt(0).toUpperCase() + (block.blockType || 'unknown').slice(1)}
                        </span>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {index > 0 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); moveBlock(index, index - 1); }}
                              title="Move Up"
                            className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-600 dark:text-blue-400"
                            >
                              <ArrowUpIcon className="h-4 w-4" />
                            </button>
                          )}
                          {index < selectedChapter.contentBlocks.length - 1 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); moveBlock(index, index + 1); }}
                              title="Move Down"
                            className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-600 dark:text-blue-400"
                            >
                              <ArrowDownIcon className="h-4 w-4" />
                            </button>
                          )}
                           <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteBlock(block.index !== undefined ? block.index : index); }}
                              title="Delete Block"
                          className="p-1 rounded hover:bg-red-200 dark:hover:bg-red-700 text-red-500 dark:text-red-400"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                        </div>
                      </div>
                    </li>
                ))
            ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No content blocks added yet.</p>
              </div>
            )}
            </ul>
          </div>

          {/* Column 2: Content Block Editor & Feedback Prompt */}
          <div className="lg:w-3/4 flex flex-col space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 flex-grow">
              <h2 className="text-lg font-heading dark:text-white mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">Editor</h2>
                {selectedBlock ? (
                  <ContentBlockEditor
                    key={selectedBlock._id}
                    chapterId={selectedChapter._id}
                    block={selectedBlock}
                    onUpdate={handleUpdateBlock}
                  onDelete={(blockIndex) => handleDeleteBlock(blockIndex)}
                  />
                ) : (
                   <div className="text-center py-10">
                  <p className="text-gray-500 dark:text-gray-400">Select a content block to edit.</p>
                  </div>
                )}
              </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <h2 className="text-lg font-heading dark:text-white mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">Feedback Prompt</h2>
                  <ContentPromptEditor
                      key={selectedChapter._id}
                feedbackPrompt={selectedChapter.llmConfig?.feedbackPrompt || ''}
                      onUpdate={handleFeedbackPromptUpdate}
                  />
              </div>
          </div>
        </div>
      )}

      {currentView === 'users' && (
        <UserManagementView token={token} setError={setError} />
      )}

      {currentView === 'usage' && (
        <UsageStatsView token={token} setError={setError} />
      )}

      {currentView === 'prompts' && (
        <div className="h-full">
          {editingTemplate ? (
            <TemplateEditor
              template={editingTemplate}
              variables={templateVariables}
              onSave={async (templateData) => {
                try {
                  const url = editingTemplate._id
                    ? `${import.meta.env.VITE_API_URL}/api/admin/prompts/${editingTemplate._id}`
                    : `${import.meta.env.VITE_API_URL}/api/admin/prompts`;
                  
                  const method = editingTemplate._id ? 'PUT' : 'POST';
                  
                  const response = await fetch(url, {
                    method,
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(templateData)
                  });

                  if (!response.ok) {
                    // Try to parse JSON error, but handle non-JSON responses
                    let errorMessage = 'Failed to save template';
                    try {
                      const errorData = await response.json();
                      errorMessage = errorData.error || errorMessage;
                    } catch (parseError) {
                      // If response is not JSON, try to get text
                      try {
                        const errorText = await response.text();
                        errorMessage = errorText || errorMessage;
                      } catch (textError) {
                        // If all else fails, use status text
                        errorMessage = response.statusText || errorMessage;
                      }
                    }
                    throw new Error(errorMessage);
                  }
                  
                  // Parse response JSON
                  const result = await response.json();

                    setEditingTemplate(null);
                    setSelectedTemplate(null);
                    // Refresh template list after save
                    setTemplateRefreshKey(prev => prev + 1);
                } catch (err) {
                  logger.error('Error saving template:', err);
                  setError(err.message || 'Failed to save template');
                }
              }}
              onCancel={() => {
                setEditingTemplate(null);
                setSelectedTemplate(null);
              }}
            />
          ) : (
            <TemplateLibrary
              key={templateRefreshKey}
              refreshKey={templateRefreshKey}
              onSelectTemplate={(template) => {
                setSelectedTemplate(template);
                setTemplateVariables(template.variables || []);
              }}
              onEdit={(template) => {
                setEditingTemplate(template);
                setTemplateVariables(template.variables || []);
              }}
              onDelete={(template) => {
                setError('');
                setDeleteConfirmation({ kind: 'promptTemplate', template });
              }}
              onCreateNew={() => {
                setEditingTemplate({
                  feature: 'chat',
                  name: '',
                  description: '',
                  template: '',
                  variables: []
                });
                setTemplateVariables([]);
              }}
              onCompare={(template1, template2) => {
                // Ensure both templates are of the same feature type
                if (template1.feature !== template2.feature) {
                  setError(`Cannot compare templates of different features. ${template1.feature} vs ${template2.feature}`);
                  return;
                }
                setComparingTemplates({
                  template1,
                  template2
                });
              }}
            />
          )}

          {/* Template Detail Modal */}
          {selectedTemplate && !editingTemplate && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {selectedTemplate.name}
                  </h2>
                  <button
                    onClick={() => setSelectedTemplate(null)}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Feature</label>
                    <p className="text-gray-900 dark:text-white">{selectedTemplate.feature}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Version</label>
                    <p className="text-gray-900 dark:text-white">v{selectedTemplate.versionNumber}</p>
                  </div>
                  {selectedTemplate.description && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                      <p className="text-gray-900 dark:text-white">{selectedTemplate.description}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Template</label>
                    <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                      {stripHtmlForDisplay(selectedTemplate.template)}
                    </div>
                  </div>
                  {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Variables</label>
                      <ul className="mt-2 space-y-1">
                        {selectedTemplate.variables.map((variable, idx) => (
                          <li key={idx} className="text-sm text-gray-900 dark:text-white">
                            <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{`{{${variable.name}}}`}</code>
                            {variable.description && (
                              <span className="ml-2 text-gray-600 dark:text-gray-400">- {variable.description}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setTestingTemplate(selectedTemplate);
                        setSelectedTemplate(null);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Test Template
                    </button>
                    <button
                      onClick={() => {
                        setEditingTemplate(selectedTemplate);
                        setSelectedTemplate(null);
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Template Comparison Modal */}
          {comparingTemplates && (
            <TemplateComparison
              template1={comparingTemplates.template1}
              template2={comparingTemplates.template2}
              onClose={() => setComparingTemplates(null)}
            />
          )}

          {/* Template Tester Modal */}
          {testingTemplate && (
            <TemplateTester
              template={testingTemplate}
              onClose={() => setTestingTemplate(null)}
            />
          )}
        </div>
      )}

      {currentView === 'deliverables' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
            <h2 className="text-xl font-heading dark:text-white">Document Templates For User Deliverables</h2>
            <button
              onClick={() => handleOpenDocumentTemplateModal()}
              className="flex items-center bg-black dark:bg-white text-white dark:text-black px-4 py-2 rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-2" /> Add Template
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Users can generate these documents after completing all required chapters. Admin inputs are not shown to users.
          </p>
          {documentTemplates.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 py-8">No document templates yet. Add one to get started.</p>
          ) : (
            <ul className="space-y-3">
              {documentTemplates.map((tpl) => (
                <li
                  key={tpl._id}
                  className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50"
                >
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{tpl.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {tpl.documentType.toUpperCase()} · {Array.isArray(tpl.requiredChapterIds) ? tpl.requiredChapterIds.length : 0} required chapter(s)
                    </div>
                    {tpl.description && (
                      <div className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-1">{tpl.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleOpenDocumentTemplateModal(tpl)}
                      className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => requestDeleteDocumentTemplate(tpl)}
                      className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Document Template Add/Edit Modal - z-[70] so it appears above Oberon (z-[60]) */}
      {showDocumentTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <h2 className="text-xl font-heading dark:text-white">
                {editingDocumentTemplate ? 'Edit Document Template' : 'Add Document Template'}
              </h2>
              <button
                onClick={() => { setShowDocumentTemplateModal(false); setEditingDocumentTemplate(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSaveDocumentTemplate} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                <input
                  type="text"
                  value={documentTemplateForm.name}
                  onChange={(e) => setDocumentTemplateForm({ ...documentTemplateForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={documentTemplateForm.description}
                  onChange={(e) => setDocumentTemplateForm({ ...documentTemplateForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document type *</label>
                <select
                  value={documentTemplateForm.documentType}
                  onChange={(e) => setDocumentTemplateForm({ ...documentTemplateForm, documentType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="pdf">PDF</option>
                  <option value="word">Word</option>
                  <option value="excel">Excel</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Required chapters (user must complete all)</label>
                <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 p-2 space-y-1">
                  {chaptersSortedForTemplates.map((ch) => {
                    const id = ch._id?.toString?.() ?? ch._id;
                    const currentIds = documentTemplateForm.requiredChapterIds || [];
                    const selected = currentIds.some((c) => (c?.toString?.() ?? c) === id);
                    return (
                      <label
                        key={ch._id}
                        className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {
                            const current = documentTemplateForm.requiredChapterIds || [];
                            const next = selected
                              ? current.filter((c) => (c?.toString?.() ?? c) !== id)
                              : [...current, id];
                            setDocumentTemplateForm({ ...documentTemplateForm, requiredChapterIds: next });
                          }}
                          className="rounded border-gray-300 dark:border-gray-600 text-black dark:text-white"
                        />
                        <span className="text-sm text-gray-900 dark:text-white">
                          {ch.title}
                          {ch.moduleId?.name ? (
                            <span className="text-gray-500 dark:text-gray-400"> — {ch.moduleId.name}</span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Select any chapters that must be completed before this deliverable is available.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">AI instructions (prompt for generation)</label>
                <textarea
                  value={documentTemplateForm.aiInstructions}
                  onChange={(e) => setDocumentTemplateForm({ ...documentTemplateForm, aiInstructions: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={4}
                  placeholder="Describe what the AI should produce, e.g. sections, tone, structure. Chapter content will be included automatically."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use Handlebars placeholders (replaced at generation time). Available variables:
                </p>
                <pre className="mt-1 p-2 rounded bg-gray-100 dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 font-mono overflow-x-auto">
{`{{chapterContent}}     — Concatenated titles/descriptions of required chapters
{{userAnswers}}       — JSON: per-chapter prompts and user answers
{{templateName}}      — This template’s name
{{templateDescription}} — This template’s description
{{adminInputs}}       — Object from Admin inputs (e.g. {{adminInputs.audience}})`}
                </pre>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Admin inputs (JSON, not shown to users)</label>
                <textarea
                  value={documentTemplateForm.adminInputsRaw}
                  onChange={(e) => setDocumentTemplateForm({ ...documentTemplateForm, adminInputsRaw: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                  rows={3}
                  placeholder='e.g. {"audience": "investors", "tone": "professional"}'
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Leave empty for none. Use valid JSON key-value pairs. Example: {"{"} "audience": "investors", "tone": "professional" {"}"}</p>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={documentTemplateForm.isActive}
                    onChange={(e) => setDocumentTemplateForm({ ...documentTemplateForm, isActive: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 dark:text-gray-300">Order</label>
                  <input
                    type="number"
                    value={documentTemplateForm.order}
                    onChange={(e) => setDocumentTemplateForm({ ...documentTemplateForm, order: Number(e.target.value) || 0 })}
                    className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min={0}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => { setShowDocumentTemplateModal(false); setEditingDocumentTemplate(null); }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md hover:bg-gray-800 dark:hover:bg-gray-200">
                  {editingDocumentTemplate ? 'Save changes' : 'Create template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
              <h2 className="text-xl font-heading dark:text-white">
                Create New {editingItemType === 'unit' ? 'Unit' : editingItemType === 'module' ? 'Module' : 'Chapter'}
              </h2>
              <button
                onClick={handleCloseCreateModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreateItem} className="p-6 space-y-4">
              {/* Name/Title Field */}
              <div>
                <label htmlFor="item-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {editingItemType === 'chapter' ? 'Title' : 'Name'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="item-name"
                  value={editingItemType === 'chapter' ? newItemForm.title : newItemForm.name}
                  onChange={(e) => {
                    if (editingItemType === 'chapter') {
                      setNewItemForm({ ...newItemForm, title: e.target.value });
                    } else {
                      setNewItemForm({ ...newItemForm, name: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  placeholder={`Enter ${editingItemType === 'chapter' ? 'chapter title' : editingItemType === 'unit' ? 'unit name' : 'module name'}`}
                  required
                  autoFocus
                />
              </div>

              {/* Description Field */}
              <div>
                <label htmlFor="item-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description <span className="text-xs text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="item-description"
                  value={newItemForm.description}
                  onChange={(e) => setNewItemForm({ ...newItemForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  placeholder={`Enter ${editingItemType} description (optional)`}
                  rows={3}
                />
              </div>

              {/* Unit Selection (for Module) */}
              {editingItemType === 'module' && (
                <div>
                  <label htmlFor="module-unit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Unit <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="module-unit"
                    value={newItemForm.unitId}
                    onChange={(e) => setNewItemForm({ ...newItemForm, unitId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                    required
                  >
                    <option value="">Select a unit</option>
                    {units.map(unit => (
                      <option key={unit._id} value={unit._id}>{unit.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Module Selection (for Chapter) */}
              {editingItemType === 'chapter' && (
                <div>
                  <label htmlFor="chapter-module" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Module <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="chapter-module"
                    value={newItemForm.moduleId}
                    onChange={(e) => setNewItemForm({ ...newItemForm, moduleId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                    required
                  >
                    <option value="">Select a module</option>
                    {selectedUnit && modules.filter(m => (m.unitId?._id || m.unitId) === selectedUnit._id).map(module => (
                      <option key={module._id} value={module._id}>{module.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Color Selection (for Module only) */}
              {editingItemType === 'module' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Color
                  </label>
                  {/* Color preview circles */}
                  <div className="flex gap-3 flex-wrap">
                    {['gray', 'blue', 'green', 'purple', 'yellow', 'pink', 'indigo', 'orange', 'teal'].map((color) => {
                      const colorClasses = {
                        gray: 'bg-gray-500',
                        blue: 'bg-blue-500',
                        green: 'bg-green-500',
                        purple: 'bg-purple-500',
                        yellow: 'bg-yellow-500',
                        pink: 'bg-pink-500',
                        indigo: 'bg-indigo-500',
                        orange: 'bg-orange-500',
                        teal: 'bg-teal-500'
                      };
                      return (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewItemForm({ ...newItemForm, color })}
                          className={`w-10 h-10 rounded-full ${colorClasses[color]} border-2 ${
                            newItemForm.color === color ? 'border-gray-900 dark:border-white ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-500' : 'border-gray-300 dark:border-gray-600'
                          } transition-all hover:scale-110`}
                          title={color.charAt(0).toUpperCase() + color.slice(1)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Order Field */}
              <div>
                <label htmlFor="item-order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Order <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="item-order"
                  value={newItemForm.order}
                  onChange={(e) => setNewItemForm({ ...newItemForm, order: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  placeholder="Order number"
                  required
                  min="0"
                />
              </div>

              {/* Required Checkbox (for Chapter only) */}
              {editingItemType === 'chapter' && (
                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newItemForm.isRequired}
                      onChange={(e) => setNewItemForm({ ...newItemForm, isRequired: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Required Chapter
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                    Required chapters must be completed before the next chapter unlocks. Optional chapters can be skipped.
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-2 rounded text-sm">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseCreateModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                >
                  Create {editingItemType === 'unit' ? 'Unit' : editingItemType === 'module' ? 'Module' : 'Chapter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
              <h2 className="text-xl font-heading dark:text-white">
                Edit {editingItemType === 'unit' ? 'Unit' : editingItemType === 'module' ? 'Module' : 'Chapter'}
              </h2>
              <button
                onClick={handleCloseEditModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSaveEdit} className="p-6 space-y-4">
              {/* Name/Title Field */}
              <div>
                <label htmlFor="edit-item-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {editingItemType === 'chapter' ? 'Title' : 'Name'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="edit-item-name"
                  value={editingItemType === 'chapter' ? editItemForm.title : editItemForm.name}
                  onChange={(e) => {
                    if (editingItemType === 'chapter') {
                      setEditItemForm({ ...editItemForm, title: e.target.value });
                    } else {
                      setEditItemForm({ ...editItemForm, name: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  placeholder={`Enter ${editingItemType === 'chapter' ? 'chapter title' : editingItemType === 'unit' ? 'unit name' : 'module name'}`}
                  required
                  autoFocus
                />
              </div>

              {/* Description Field */}
              <div>
                <label htmlFor="edit-item-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description <span className="text-xs text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="edit-item-description"
                  value={editItemForm.description}
                  onChange={(e) => setEditItemForm({ ...editItemForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  placeholder={`Enter ${editingItemType} description (optional)`}
                  rows={3}
                />
              </div>

              {/* Unit Selection (for Module) */}
              {editingItemType === 'module' && (
                <div>
                  <label htmlFor="edit-module-unit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Unit <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="edit-module-unit"
                    value={editItemForm.unitId}
                    onChange={(e) => setEditItemForm({ ...editItemForm, unitId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                    required
                  >
                    <option value="">Select a unit</option>
                    {units.map(unit => (
                      <option key={unit._id} value={unit._id}>{unit.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Module Selection (for Chapter) */}
              {editingItemType === 'chapter' && (
                <div>
                  <label htmlFor="edit-chapter-module" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Module <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="edit-chapter-module"
                    value={editItemForm.moduleId}
                    onChange={(e) => setEditItemForm({ ...editItemForm, moduleId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                    required
                  >
                    <option value="">Select a module</option>
                    {modules.map(module => (
                      <option key={module._id} value={module._id}>{module.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Color Selection (for Module only) */}
              {editingItemType === 'module' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Color
                  </label>
                  {/* Color preview circles */}
                  <div className="flex gap-3 flex-wrap">
                    {['gray', 'blue', 'green', 'purple', 'yellow', 'pink', 'indigo', 'orange', 'teal'].map((color) => {
                      const colorClasses = {
                        gray: 'bg-gray-500',
                        blue: 'bg-blue-500',
                        green: 'bg-green-500',
                        purple: 'bg-purple-500',
                        yellow: 'bg-yellow-500',
                        pink: 'bg-pink-500',
                        indigo: 'bg-indigo-500',
                        orange: 'bg-orange-500',
                        teal: 'bg-teal-500'
                      };
                      return (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setEditItemForm({ ...editItemForm, color })}
                          className={`w-10 h-10 rounded-full ${colorClasses[color]} border-2 ${
                            editItemForm.color === color ? 'border-gray-900 dark:border-white ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-500' : 'border-gray-300 dark:border-gray-600'
                          } transition-all hover:scale-110`}
                          title={color.charAt(0).toUpperCase() + color.slice(1)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Order Field */}
              <div>
                <label htmlFor="edit-item-order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Order <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="edit-item-order"
                  value={editItemForm.order}
                  onChange={(e) => setEditItemForm({ ...editItemForm, order: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  placeholder="Order number"
                  required
                  min="0"
                />
              </div>

              {/* Required Checkbox (for Chapter only) */}
              {editingItemType === 'chapter' && (
                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editItemForm.isRequired}
                      onChange={(e) => setEditItemForm({ ...editItemForm, isRequired: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Required Chapter
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                    Required chapters must be completed before the next chapter unlocks. Optional chapters can be skipped.
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-2 rounded text-sm">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmation && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
          aria-describedby="delete-confirm-desc"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full border border-gray-200 dark:border-gray-700">
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
              <h2 id="delete-confirm-title" className="text-lg font-heading font-semibold text-gray-900 dark:text-white">
                {getDeleteModalCopy().title}
              </h2>
              <p id="delete-confirm-desc" className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {getDeleteModalCopy().body}
              </p>
            </div>
            <div className="p-4 flex justify-end gap-3">
              <button
                type="button"
                disabled={deleteInProgress}
                onClick={() => {
                  if (!deleteInProgress) setDeleteConfirmation(null);
                }}
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteInProgress}
                onClick={() => void confirmPendingDelete()}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteInProgress ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminDashboard;